/**
 * Where a notification tap lands. A push carries a URL — `/?agent=<id>&message=<id>`,
 * `/?task=<id>` or `/?view=<name>` — which reaches the app one of two ways: as the
 * address it was opened at (no window was open), or as an `open-url` message from the
 * service worker to the window it focused. Both go through `parseDeepLink`, so they
 * land in the same place. The parameters are dropped from the address once read, so
 * a reload does not jump again.
 */
export type DeepLink =
  | { kind: "agent"; agentId: string; messageId: string | null }
  | { kind: "task"; taskId: string }
  | { kind: "view"; view: string };

const ID = /^[A-Za-z0-9_-]{1,64}$/;
const id = (value: string | null): string | null => (value && ID.test(value) ? value : null);

export function parseDeepLink(search: string): DeepLink | null {
  const params = new URLSearchParams(search);
  const agentId = id(params.get("agent"));
  if (agentId) return { kind: "agent", agentId, messageId: id(params.get("message")) };
  const taskId = id(params.get("task"));
  if (taskId) return { kind: "task", taskId };
  const view = params.get("view");
  if (view && /^[a-z]{1,20}$/.test(view)) return { kind: "view", view };
  return null;
}

const LINK_PARAMS = ["agent", "message", "task"];

/** The deep link the app was opened at, if any, taken off the address. `view` is left to the shortcut reader. */
export function takeDeepLinkFromLocation(): DeepLink | null {
  const url = new URL(window.location.href);
  const link = parseDeepLink(url.search);
  if (!link || link.kind === "view") return null;
  for (const name of LINK_PARAMS) url.searchParams.delete(name);
  window.history.replaceState(window.history.state, "", url.toString());
  return link;
}

/** Calls `handler` for each notification tapped while a window was open. */
export function onServiceWorkerLink(handler: (link: DeepLink) => void): () => void {
  if (!("serviceWorker" in navigator)) return () => undefined;
  const listener = (event: MessageEvent) => {
    const data = event.data as { type?: string; url?: string } | null;
    if (data?.type !== "open-url" || typeof data.url !== "string") return;
    const link = parseDeepLink(new URL(data.url, window.location.origin).search);
    if (link) handler(link);
  };
  navigator.serviceWorker.addEventListener("message", listener);
  return () => navigator.serviceWorker.removeEventListener("message", listener);
}
